import React, { useCallback, useEffect, useState } from 'react';
import {
  Card,
  Form,
  Input,
  Button,
  Space,
  Typography,
  message,
  Skeleton,
  Divider,
  Switch,
  Select,
  Row,
  Col,
  Tag,
} from 'antd';
import {
  DollarOutlined,
  ReloadOutlined,
  SaveOutlined,
  FileTextOutlined,
  CheckCircleOutlined,
} from '@ant-design/icons';
import MainLayout from '../../../layouts/MainLayout';
import { superadminGlobalInvoiceSettingsApi, show422Error } from '../../../utils/api';
import { PANEL_PRIMARY_BTN_CLASS } from '../../../constants/ui';

const { Text } = Typography;
const { TextArea } = Input;

const TEMPLATE_OPTIONS = [
  { value: 'invoice-1', label: 'قالب ۱', color: '#1890ff' },
  { value: 'invoice-2', label: 'قالب ۲', color: '#52c41a' },
  { value: 'invoice-3', label: 'قالب ۳', color: '#722ed1' },
  { value: 'invoice-4', label: 'قالب ۴', color: '#fa8c16' },
  { value: 'invoice-5', label: 'قالب ۵', color: '#eb2f96' },
];

const LOCALE_OPTIONS = [
  { value: 'fa', label: 'فارسی (fa)' },
  { value: 'en', label: 'English (en)' },
  { value: 'zh', label: '中文 (zh)' },
];

const SuperadminGlobalInvoiceSettings = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [logoUrl, setLogoUrl] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await superadminGlobalInvoiceSettingsApi.get();
      const data = res?.data || {};
      setLogoUrl(data.logo_url || '');
      setSelectedTemplate(data.template || null);
      form.setFieldsValue({
        locale: data.locale || 'en',
        show_authorised_signatory: Number(data.authorised_signatory) === 1,
        billing_name: data.billing_name || '',
        billing_tax_name: data.billing_tax_name || '',
        billing_tax_id: data.billing_tax_id || '',
        billing_address: data.billing_address || '',
        invoice_terms: data.invoice_terms || '',
        template: data.template || undefined,
      });
    } catch (error) {
      message.error(error?.message || 'خطا در دریافت تنظیمات مالی');
    } finally {
      setLoading(false);
    }
  }, [form]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const onSubmit = async (values) => {
    const template = selectedTemplate || values.template;
    if (!template) {
      message.warning('قالب فاکتور را انتخاب کنید');
      return;
    }
    setSubmitting(true);
    try {
      await superadminGlobalInvoiceSettingsApi.update({
        locale: values.locale,
        show_authorised_signatory: values.show_authorised_signatory ? 1 : 0,
        template,
        billing_name: values.billing_name?.trim() || '',
        billing_tax_name: values.billing_tax_name?.trim() || '',
        billing_tax_id: values.billing_tax_id?.trim() || '',
        billing_address: values.billing_address?.trim() || '',
        invoice_terms: values.invoice_terms || '',
      });
      message.success('تنظیمات مالی ذخیره شد');
      fetchData();
    } catch (error) {
      if (error?.name === 'ValidationError' || error?.details) show422Error(error, form);
      else message.error(error?.message || 'خطا در ذخیره');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <MainLayout>
      <div className="custom-font w-full min-w-0" style={{ fontFamily: 'CustomFont' }}>
        <Card className="shadow-xl border-0" styles={{ body: { padding: 0 } }}>
          <div className="flex flex-wrap items-center justify-between gap-4 border-b border-gray-200 bg-white p-4 sm:p-6 dark:border-gray-700 dark:bg-gray-800 rounded-t-xl">
            <div className="flex min-w-0 items-center gap-3">
              <div className="flex h-11 w-11 sm:h-12 sm:w-12 flex-shrink-0 items-center justify-center rounded-xl bg-amber-50 dark:bg-amber-900/25">
                <DollarOutlined className="text-lg sm:text-xl text-amber-600 dark:text-amber-400" />
              </div>
              <div className="min-w-0">
                <h1 className="m-0 text-xl sm:text-2xl font-bold text-gray-800 dark:text-gray-100">تنظیمات مالی</h1>
                <Text type="secondary" className="text-xs sm:text-sm">
                  قالب و اطلاعات صورتحساب سراسری
                </Text>
              </div>
            </div>
            <Button icon={<ReloadOutlined />} onClick={fetchData} loading={loading}>
              بروزرسانی
            </Button>
          </div>

          <div className="rounded-b-xl bg-gray-50 p-4 sm:p-6 dark:bg-gray-900/50">
            <Card className="border border-gray-100 shadow-sm dark:border-gray-700 dark:bg-gray-800">
              {loading ? (
                <Skeleton active paragraph={{ rows: 12 }} />
              ) : (
                <Form form={form} layout="vertical" onFinish={onSubmit}>
                  {logoUrl ? (
                    <div className="mb-6">
                      <Text type="secondary" className="text-xs block mb-2">
                        لوگوی فعلی
                      </Text>
                      <img src={logoUrl} alt="" className="max-h-16 object-contain rounded-lg border border-gray-200 dark:border-gray-600" />
                    </div>
                  ) : null}

                  <Card
                    size="small"
                    className="mb-6 border border-gray-100 dark:border-gray-700"
                    title={
                      <span>
                        <FileTextOutlined className="ml-2 text-blue-500" />
                        انتخاب قالب فاکتور
                      </span>
                    }
                  >
                    <Row gutter={[12, 12]}>
                      {TEMPLATE_OPTIONS.map((t) => {
                        const active = selectedTemplate === t.value;
                        return (
                          <Col xs={12} sm={8} md={6} lg={4} key={t.value}>
                            <button
                              type="button"
                              onClick={() => {
                                setSelectedTemplate(t.value);
                                form.setFieldValue('template', t.value);
                              }}
                              className="w-full cursor-pointer rounded-xl border-2 p-3 text-center transition-all dark:bg-gray-900/30"
                              style={{
                                borderColor: active ? t.color : 'var(--ant-color-border)',
                                backgroundColor: active ? `${t.color}12` : undefined,
                              }}
                            >
                              <FileTextOutlined style={{ fontSize: 22, color: active ? t.color : '#bfbfbf' }} />
                              <div className="mt-2 text-sm font-semibold" style={{ color: active ? t.color : undefined }}>
                                {t.label}
                              </div>
                              {active ? (
                                <Tag icon={<CheckCircleOutlined />} color="success" className="mt-2 text-xs">
                                  فعال
                                </Tag>
                              ) : null}
                            </button>
                          </Col>
                        );
                      })}
                    </Row>
                    <Form.Item name="template" hidden rules={[{ required: true }]}>
                      <Input />
                    </Form.Item>
                  </Card>

                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <Form.Item name="locale" label="زبان فاکتور" rules={[{ required: true }]}>
                      <Select options={LOCALE_OPTIONS} popupClassName="custom-font" />
                    </Form.Item>
                    <Form.Item name="show_authorised_signatory" label="نمایش امضای مجاز" valuePropName="checked">
                      <Switch />
                    </Form.Item>
                  </div>

                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <Form.Item name="billing_name" label="نام صورتحساب" rules={[{ required: true, message: 'نام را وارد کنید' }]}>
                      <Input />
                    </Form.Item>
                    <Form.Item name="billing_tax_name" label="نام مالیاتی">
                      <Input />
                    </Form.Item>
                    <Form.Item name="billing_tax_id" label="شناسه مالیاتی">
                      <Input dir="ltr" />
                    </Form.Item>
                  </div>

                  <Form.Item name="billing_address" label="آدرس صورتحساب">
                    <TextArea rows={3} />
                  </Form.Item>

                  <Form.Item name="invoice_terms" label="شرایط فاکتور">
                    <TextArea rows={4} />
                  </Form.Item>

                  <Divider />
                  <Form.Item className="mb-0">
                    <Space wrap>
                      <Button
                        type="primary"
                        htmlType="submit"
                        loading={submitting}
                        icon={<SaveOutlined />}
                        className={PANEL_PRIMARY_BTN_CLASS}
                      >
                        ذخیره تغییرات
                      </Button>
                    </Space>
                  </Form.Item>
                </Form>
              )}
            </Card>
          </div>
        </Card>
      </div>
    </MainLayout>
  );
};

export default SuperadminGlobalInvoiceSettings;
